import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword,
    updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser || null);

            if (firebaseUser) {
                // Buscar perfil do usuário no Firestore
                const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
                if (userDoc.exists()) {
                    setUserProfile(userDoc.data());
                }
            } else {
                setUserProfile(null);
            }

            setAuthLoading(false);
        });
        return () => unsub();
    }, []);

    const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

    const register = async (email, password, name, plan = 'basico') => {
        // Criar usuário no Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Atualizar nome do perfil
        await updateProfile(user, { displayName: name });

        // Criar documento do usuário no Firestore
        await setDoc(doc(db, 'users', user.uid), {
            name,
            email,
            plan,
            subscriptionStatus: plan === 'basico' ? 'active' : 'pending',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });

        return user;
    };

    const logout = () => signOut(auth);

    const updateUserPlan = async (plan, subscriptionStatus = 'active') => {
        if (!user) return;

        await setDoc(doc(db, 'users', user.uid), {
            plan,
            subscriptionStatus,
            updatedAt: new Date().toISOString(),
        }, { merge: true });

        setUserProfile(prev => ({
            ...prev,
            plan,
            subscriptionStatus,
        }));
    };

    return (
        <AuthContext.Provider value={{
            user,
            userProfile,
            authLoading,
            login,
            register,
            logout,
            updateUserPlan,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
